/**
 * QuestionnaireShelf — "Your questionnaires": upload + parse-status shelf for
 * the therapist's custom questionnaire files (Plan 2, Task 2; list lifted to
 * the parent in Task 4).
 *
 * Upload flow: requestUpload -> presigned S3 PUT (progress) -> startParse ->
 * pollParse -> shared refresh. The presigned PUT is signed for Content-Type
 * application/octet-stream (see useQuestionnaireFiles.ts's requestUpload
 * comment) — sending any other Content-Type mismatches the signature and S3
 * rejects with SignatureDoesNotMatch. This PUT sets that exact header value,
 * matching src/features/library/useLibraryApi.ts's uploadFile PUT. Progress
 * needs byte-level callbacks, which fetch() can't give us on the request
 * body in every supported browser, so this uses XMLHttpRequest instead of
 * fetch (the only difference from the library's PUT).
 *
 * Once startParse succeeds the (still-uploading) row is already visible via
 * the shared refresh; polling to a terminal status runs detached (like
 * library/index.tsx's trackProcessing) so the upload button re-enables
 * immediately and a second upload isn't blocked behind a multi-minute parse.
 *
 * List ownership (Plan 2 Task 4): the template list (`items`) is now fetched
 * ONCE by the parent (index.tsx, via useQuestionnaireFiles().list()) and
 * passed down as a prop, shared with SendQuestionnaireDialog — so an upload
 * here is immediately visible in the send picker with no separate fetch or
 * staleness window. This component still owns the mutating calls
 * (requestUpload/startParse/pollParse/remove) via its own useQuestionnaireFiles()
 * instance, and asks the parent to refresh the shared list (`onRefresh`)
 * after each mutation rather than maintaining its own copy of the list.
 * Delete is optimistic (`hiddenIds`) since the parent's refresh is async and
 * a stale list read shouldn't resurrect a row the therapist just removed;
 * hiddenIds reconciles against `items` once a fresh list actually reflects
 * the deletion (or is cleared immediately on a failed delete).
 *
 * Review/Send are host callbacks: Task 3 built the review screen, Task 4
 * wires the send dialog. This shelf only decides WHEN to show each action
 * (needs_review / ready) and hands off the fileId or template.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Chip } from "../../components/ui";
import type { ChipTone } from "../../components/ui";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useQuestionnaireFiles } from "./useQuestionnaireFiles";
import type { QuestionnaireTemplate, QuestionnaireTemplateStatus } from "./useQuestionnaireFiles";
import "./QuestionnaireShelf.css";

const ACCEPT = ".pdf,.txt,.md,.xlsx,.docx";
const MAX_BYTES = 7 * 1024 * 1024;

export type QuestionnaireShelfProps = {
  items: QuestionnaireTemplate[];
  loading: boolean;
  onRefresh: (signal?: AbortSignal) => Promise<void>;
  onReview: (fileId: string) => void;
  onSend: (template: QuestionnaireTemplate) => void;
};

// Exported for the test file: lets a test swap in a fake XHR without
// reaching into module internals.
export function putToS3(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    // Presigned for application/octet-stream (get_upload_url.py) — any other
    // Content-Type mismatches the signature. Match the library PUT exactly.
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        console.error("[QuestionnaireShelf] S3 upload failed:", xhr.status, xhr.responseText);
        reject(new Error("qshelf.uploadFailed"));
      }
    };
    xhr.onerror = () => {
      console.error("[QuestionnaireShelf] S3 upload network error");
      reject(new Error("qshelf.uploadFailed"));
    };
    xhr.send(file);
  });
}

function statusTone(status: QuestionnaireTemplateStatus): ChipTone {
  switch (status) {
    case "ready":
      return "ok";
    case "needs_review":
      return "warn";
    case "parse_failed":
      return "bad";
    default:
      return "muted"; // parsing
  }
}

export const QuestionnaireShelf: React.FC<QuestionnaireShelfProps> = ({ items, loading, onRefresh, onReview, onSend }) => {
  const { t } = useTranslation();
  const api = useQuestionnaireFiles();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Cancels every detached parse poll on unmount (the shared list-load's
  // own AbortController lives in the parent now).
  const unmountAbortRef = useRef<AbortController>(new AbortController());
  useEffect(() => () => unmountAbortRef.current.abort(), []);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Record<string, "delete" | "parsing">>({});
  const [deleteTarget, setDeleteTarget] = useState<QuestionnaireTemplate | null>(null);
  // Optimistically-hidden rows (delete in flight / just confirmed) — see the
  // module docstring for why this can't just be local `items` state anymore.
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // Once a fresh `items` list no longer contains a hidden id, drop it from
  // the set too (nothing left to reconcile against).
  useEffect(() => {
    setHiddenIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      let changed = false;
      for (const id of Array.from(next)) {
        if (!items.some((it) => it.fileId === id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [items]);

  const visibleItems = hiddenIds.size ? items.filter((it) => !hiddenIds.has(it.fileId)) : items;

  // Coded errors ("qshelf.*" i18n keys, thrown by putToS3) get translated;
  // backend/QuestionnaireFilesApiError messages surface verbatim (loud).
  const errText = useCallback((e: any, fallbackKey: string): string => {
    const msg = e?.message ? String(e.message) : "";
    if (msg.startsWith("qshelf.")) return t(msg);
    return msg || t(fallbackKey);
  }, [t]);

  // Detached per-file poll: runs after the upload button is already free.
  const trackParse = useCallback(async (fileId: string, runId: string) => {
    const signal = unmountAbortRef.current.signal;
    setBusyIds((m) => ({ ...m, [fileId]: "parsing" }));
    try {
      await api.pollParse(fileId, runId, { signal });
    } catch (e: any) {
      if (!signal.aborted) {
        console.error("[QuestionnaireShelf] pollParse:", e);
        setError(errText(e, "qshelf.parseFailed"));
      }
    } finally {
      setBusyIds((m) => {
        const { [fileId]: _drop, ...rest } = m;
        return rest;
      });
      if (!signal.aborted) await onRefresh(signal);
    }
  }, [api, errText, onRefresh]);

  const onPickFile = async (file: File) => {
    setError(null);

    if (file.size > MAX_BYTES) {
      setError(t("qshelf.tooLarge"));
      return; // loud, no upload attempt
    }

    setUploading(true);
    setUploadProgress(0);
    let fileId: string | undefined;
    let runId: string | undefined;
    try {
      const { fileId: fid, uploadUrl } = await api.requestUpload(file.name, file.size);
      fileId = fid;
      await putToS3(uploadUrl, file, setUploadProgress);
      runId = await api.startParse(fileId, file.name);
      await onRefresh(unmountAbortRef.current.signal);
    } catch (e: any) {
      console.error("[QuestionnaireShelf] upload:", e);
      setError(errText(e, "qshelf.uploadFailed"));
      return;
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    if (fileId && runId) void trackParse(fileId, runId);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    if (busyIds[target.fileId]) return;

    setBusyIds((m) => ({ ...m, [target.fileId]: "delete" }));
    setHiddenIds((prev) => new Set(prev).add(target.fileId));

    try {
      await api.remove(target.fileId);
      await onRefresh(unmountAbortRef.current.signal);
    } catch (e: any) {
      console.error("[QuestionnaireShelf] delete:", e);
      setError(errText(e, "qshelf.deleteFailed"));
      // Rollback: the delete failed, so the row is still real — unhide it
      // immediately rather than waiting on a refresh that will never drop it.
      setHiddenIds((prev) => {
        const next = new Set(prev);
        next.delete(target.fileId);
        return next;
      });
    } finally {
      setBusyIds((m) => {
        const { [target.fileId]: _drop, ...rest } = m;
        return rest;
      });
    }
  };

  const statusLabel = (status: QuestionnaireTemplateStatus): string => {
    switch (status) {
      case "ready":
        return t("qshelf.statusReady");
      case "needs_review":
        return t("qshelf.statusNeedsReview");
      case "parse_failed":
        return t("qshelf.statusParseFailed");
      default:
        return t("qshelf.statusParsing");
    }
  };

  return (
    <div className="qs-wrap">
      <div className="qs-head">
        <h3 className="qs-title">{t("qshelf.title")}</h3>
        <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading
            ? uploadProgress != null
              ? t("qshelf.uploadingPct", { pct: uploadProgress })
              : t("qshelf.uploading")
            : t("qshelf.uploadButton")}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="qs-fileInput"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPickFile(f);
          }}
        />
      </div>

      <p className="qs-hint">{t("qshelf.hint")}</p>

      {error ? (
        <div role="alert" className="qs-error">
          {error}
        </div>
      ) : null}

      {loading && visibleItems.length === 0 ? <div className="qs-empty">{t("qshelf.loading")}</div> : null}
      {!loading && visibleItems.length === 0 ? <div className="qs-empty">{t("qshelf.empty")}</div> : null}

      <div className="qs-list">
        {visibleItems.map((it) => (
          <div key={it.fileId} className="qs-row">
            <div className="qs-rowMain">
              <div className="qs-fileName" title={it.fileName}>
                {it.fileName}
              </div>
              <Chip text={statusLabel(it.status)} tone={statusTone(it.status)} />
            </div>

            {it.status === "parse_failed" && it.parseError ? (
              <div className="qs-rowError">{it.parseError}</div>
            ) : null}

            <div className="qs-rowActions">
              {it.status === "needs_review" ? (
                <Button size="sm" onClick={() => onReview(it.fileId)} disabled={!!busyIds[it.fileId]}>
                  {t("qshelf.review")}
                </Button>
              ) : null}
              {it.status === "ready" ? (
                <Button size="sm" onClick={() => onSend(it)} disabled={!!busyIds[it.fileId]}>
                  {t("qshelf.send")}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="danger"
                onClick={() => setDeleteTarget(it)}
                disabled={!!busyIds[it.fileId]}
              >
                {t("common.delete")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={deleteTarget != null}
        title={t("qshelf.deleteTitle")}
        message={deleteTarget ? t("qshelf.deleteConfirm", { name: deleteTarget.fileName }) : ""}
        confirmLabel={t("common.delete")}
        variant="danger"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default QuestionnaireShelf;
